<template>
  <div class="worker-selector">

    <!-- 手动模式：显示流程选择 -->
    <a-form-item label="AI调用流程" v-if="!historyMode">
      <a-select
        v-model:value="localSelectedFlow"
        placeholder="请选择流程"
        :loading="flowsLoading"
        @change="handleFlowChange"
      >
        <a-select-option v-for="flow in flows" :key="flow.id" :value="flow.id">
          {{ flow.scheme_name }}
        </a-select-option>
      </a-select>
    </a-form-item>

    <a-form-item label="步骤" v-if="(localSelectedFlow || historyMode) && displaySteps.length > 0">
      <a-select
        v-model:value="localSelectedStep"
        placeholder="请选择步骤"
        :loading="stepsLoading"
        @change="handleStepChange"
      >
        <a-select-option v-for="step in displaySteps" :key="step.id" :value="step.id">
          步骤{{ step.step_order }}: {{ step.step_name }}
        </a-select-option>
      </a-select>
    </a-form-item>

    <a-form-item label="Worker" v-if="localSelectedStep && displayWorkers.length > 0">
      <a-select
        v-model:value="localSelectedWorker"
        placeholder="请选择Worker"
        :loading="workersLoading"
        @change="handleWorkerChange"
      >
        <a-select-option v-for="worker in displayWorkers" :key="worker.id" :value="worker.id">
          {{ worker.name }} (ID: {{ worker.id }})
        </a-select-option>
      </a-select>
    </a-form-item>
  </div>
</template>

<script>
import { ref, watch, computed } from 'vue';
import { getFlows, getSteps, getWorkers } from '../../api/promptSimulator';

export default {
  name: 'WorkerSelector',
  props: {
    selectedFlow: {
      type: Number,
      default: null
    },
    selectedStep: {
      type: Number,
      default: null
    },
    selectedWorker: {
      type: Number,
      default: null
    },
    historyMode: {
      type: Boolean,
      default: false
    },
    schemeSnapshot: {
      type: Object,
      default: null
    }
  },
  emits: ['update:selectedFlow', 'update:selectedStep', 'update:selectedWorker', 'worker-selected'],
  setup(props, { emit }) {
    const flows = ref([]);
    const steps = ref([]);
    const workers = ref([]);
    const flowsLoading = ref(false);
    const stepsLoading = ref(false);
    const workersLoading = ref(false);

    const localSelectedFlow = ref(props.selectedFlow);
    const localSelectedStep = ref(props.selectedStep);
    const localSelectedWorker = ref(props.selectedWorker);

    // 监听props变化
    watch(() => props.selectedFlow, (val) => {
      localSelectedFlow.value = val;
    });
    watch(() => props.selectedStep, (val) => {
      localSelectedStep.value = val;
    });
    watch(() => props.selectedWorker, (val) => {
      localSelectedWorker.value = val;
    });

    // 加载流程列表
    const loadFlows = async () => {
      flowsLoading.value = true;
      try {
        const result = await getFlows();
        if (result.code === 200) {
          flows.value = result.data || [];
        }
      } catch (error) {
        console.error('加载流程列表失败:', error);
      } finally {
        flowsLoading.value = false;
      }
    };

    // 加载步骤列表
    const loadSteps = async (flowId) => {
      if (!flowId) {
        steps.value = [];
        return;
      }
      stepsLoading.value = true;
      try {
        const result = await getSteps(flowId);
        if (result.code === 200) {
          steps.value = result.data || [];
        }
      } catch (error) {
        console.error('加载步骤列表失败:', error);
      } finally {
        stepsLoading.value = false;
      }
    };

    // 加载Worker列表
    const loadWorkers = async (stepId) => {
      if (!stepId) {
        workers.value = [];
        return;
      }
      workersLoading.value = true;
      try {
        const result = await getWorkers(stepId);
        if (result.code === 200) {
          workers.value = result.data || [];
        }
      } catch (error) {
        console.error('加载Worker列表失败:', error);
      } finally {
        workersLoading.value = false;
      }
    };

    // 流程变化处理
    const handleFlowChange = (flowId) => {
      emit('update:selectedFlow', flowId);
      localSelectedStep.value = null;
      localSelectedWorker.value = null;
      emit('update:selectedStep', null);
      emit('update:selectedWorker', null);
      loadSteps(flowId);
    };

    // 步骤变化处理
    const handleStepChange = (stepId) => {
      emit('update:selectedStep', stepId);
      localSelectedWorker.value = null;
      emit('update:selectedWorker', null);
      loadWorkers(stepId);
    };

    // Worker变化处理
    const handleWorkerChange = (workerId) => {
      emit('update:selectedWorker', workerId);
      emit('worker-selected', workerId);
    };

    // 历史模式：从快照获取步骤列表
    const getSnapshotSteps = () => {
      if (!props.schemeSnapshot || !props.schemeSnapshot.steps) {
        return [];
      }
      return props.schemeSnapshot.steps.map(stepInfo => ({
        id: stepInfo.step.id,
        step_order: stepInfo.step_order,
        step_name: stepInfo.step.step_name,
        sub_steps: stepInfo.sub_steps || []
      }));
    };

    // 历史模式：从快照获取Worker列表
    const getSnapshotWorkers = (stepId) => {
      if (!props.schemeSnapshot || !props.schemeSnapshot.steps) {
        return [];
      }
      const stepInfo = props.schemeSnapshot.steps.find(s => s.step.id === stepId);
      if (!stepInfo || !stepInfo.sub_steps) {
        return [];
      }
      return stepInfo.sub_steps.map(worker => ({
        id: worker.id,
        name: worker.step_name
      }));
    };

    // 计算显示的步骤列表（历史模式 vs 手动模式）
    const displaySteps = computed(() => {
      if (props.historyMode) {
        return getSnapshotSteps();
      }
      return steps.value;
    });

    // 计算显示的Worker列表（历史模式 vs 手动模式）
    const displayWorkers = computed(() => {
      if (props.historyMode) {
        return getSnapshotWorkers(localSelectedStep.value);
      }
      return workers.value;
    });

    // 监听历史模式变化
    watch(() => props.historyMode, (newVal) => {
      if (newVal) {
        // 切换到历史模式，清空选择
        localSelectedFlow.value = null;
        localSelectedStep.value = null;
        localSelectedWorker.value = null;
        emit('update:selectedFlow', null);
        emit('update:selectedStep', null);
        emit('update:selectedWorker', null);
      } else {
        // 切换到手动模式，重新加载流程列表
        loadFlows();
      }
    });

    // 监听快照变化
    watch(() => props.schemeSnapshot, () => {
      if (props.historyMode) {
        // 清空选择
        localSelectedStep.value = null;
        localSelectedWorker.value = null;
        emit('update:selectedStep', null);
        emit('update:selectedWorker', null);
      }
    });

    // 初始化加载流程列表（仅在非历史模式）
    if (!props.historyMode) {
      loadFlows();
    }

    return {
      flows,
      steps,
      workers,
      flowsLoading,
      stepsLoading,
      workersLoading,
      localSelectedFlow,
      localSelectedStep,
      localSelectedWorker,
      displaySteps,
      displayWorkers,
      handleFlowChange,
      handleStepChange,
      handleWorkerChange
    };
  }
};
</script>

<style scoped>
.worker-selector {
  margin-bottom: 16px;
}
</style>

